import { fetchEventSource } from '@microsoft/fetch-event-source';

import {
  CardPushEnvelopeSchema,
  SseHelloSchema,
  type AgentId,
  type CardPushEnvelope,
} from '@voll-intelligence/domain';
import { env } from '@web/core';

import type { AuthService } from '../auth/auth_service';
import { trimTrailingSlashes } from '../url';
import { createSseRetryPolicy } from './sse_retry';

interface SseSubscribeHandlers {
  onEnvelope: (envelope: CardPushEnvelope) => void;
  /** The connection's own agent id (null when the user has no agent), sent
   * once on connect via the `hello` event. */
  onHello?: (agentId: AgentId | null) => void;
}

export interface SseClient {
  /**
   * Subscribes to the org's board-push SSE stream. Returns an unsubscribe
   * function that aborts the connection. Auto-reconnects with backoff (the
   * underlying `fetchEventSource`); malformed frames are dropped.
   */
  subscribeOrgEvents: (orgId: string, handlers: SseSubscribeHandlers) => () => void;
}

/**
 * SSE client for the kanban board-push stream. Mirrors `createHttpService`:
 * defaults to the kanban base URL and pulls the bearer token from the same
 * `authService`. `EventSource` can't set an `Authorization` header, so we use
 * fetch-based SSE (`@microsoft/fetch-event-source`).
 */
export function createSseClient(
  authService: AuthService,
  baseUrl: string = env.VITE_KANBAN_API_URL,
): SseClient {
  return {
    subscribeOrgEvents: (orgId, { onEnvelope, onHello }) => {
      const ctrl = new AbortController();
      // Bounds fetchEventSource's default forever-every-1s reconnect: stops on a
      // fatal 4xx, backs off exponentially otherwise, and gives up after a few
      // consecutive failures (see sse_retry.ts).
      const retry = createSseRetryPolicy();

      void (async () => {
        try {
          const token = await authService.getAccessToken();
          if (ctrl.signal.aborted) return;
          const url = `${trimTrailingSlashes(baseUrl)}/orgs/${orgId}/events`;
          await fetchEventSource(url, {
            signal: ctrl.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            // Keep streaming while the tab is backgrounded (operators leave it open).
            openWhenHidden: true,
            onopen: retry.onopen,
            onerror: retry.onerror,
            onmessage: (ev) => {
              if (!ev.data) return; // keep-alive comments carry no data
              let payload: unknown;
              try {
                payload = JSON.parse(ev.data);
              } catch {
                return; // drop malformed frames instead of throwing
              }
              if (ev.event === 'hello') {
                const hello = SseHelloSchema.safeParse(payload);
                if (hello.success) onHello?.(hello.data.agentId);
                return;
              }
              const parsed = CardPushEnvelopeSchema.safeParse(payload);
              if (parsed.success) onEnvelope(parsed.data);
            },
          });
        } catch {
          // Swallow abort/network rejections — unsubscribe aborts the fetch,
          // which would otherwise surface as an unhandled rejection.
        }
      })();

      return () => {
        ctrl.abort();
      };
    },
  };
}
